import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@arco-design/web-react';
import { IconDelete, IconEdit } from '@arco-design/web-react/icon';

import { IconFont } from '@/components';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';

import { formatInitCommandDisplay } from '../command-args-normal';
import { CardSubtitleTooltip } from '../maas-node-groups/group-spec-card/CardSubtitleTooltip';
import { ReadonlyCommandEllipsisRow } from '../maas-node-groups/group-spec-card/ReadonlyCommandEllipsisRow';
import cardStyles from '../maas-node-groups/group-spec-card/group-spec-card.module.less';
import { formatNodeStyleCommandMultilineDisplay } from '../maas-node-groups/initCommandStartup';
import type { MaasTaskTemplateStep } from './types';
import { formatTaskStepStartupCommandModalTitle, parseTimeoutSeconds, resolveTaskStepRetryCount, resolveTaskStepTimeout } from './utils';

interface TaskStepMetric {
  key: string;
  label: string;
  value: string;
}

interface TaskStepDescriptionItem {
  key: string;
  label: string;
  value: string;
}

export function buildTaskStepMetrics(step: MaasTaskTemplateStep, t: (key: string) => string): TaskStepMetric[] {
  const timeoutSec = parseTimeoutSeconds(resolveTaskStepTimeout(step));
  return [
    ...(step.dependsOn?.length
      ? [{ key: 'dependsOn', label: t('data-center:maas.task.step.dependsOn'), value: step.dependsOn.join(', ') }]
      : []),
    { key: 'runStrategy', label: t('data-center:maas.task.step.type'), value: step.runStrategy || '-' },
    { key: 'failurePolicy', label: t('data-center:maas.task.step.errorHandling'), value: step.failurePolicy || '-' },
    { key: 'role', label: t('data-center:maas.task.step.role'), value: step.role || '-' },
    {
      key: 'timeout',
      label: t('data-center:maas.task.step.timeout'),
      value: timeoutSec != null ? String(timeoutSec) : '-',
    },
    {
      key: 'retryCount',
      label: t('data-center:maas.task.step.retryCount'),
      value: String(resolveTaskStepRetryCount(step)),
    },
  ];
}

/** cardMetrics 未展示的字段，拼接在卡片描述区 */
export function buildTaskStepDescriptionItems(step: MaasTaskTemplateStep, t: (key: string) => string): TaskStepDescriptionItem[] {
  const items: TaskStepDescriptionItem[] = [];
  if (step.groups?.length) {
    items.push({
      key: 'groups',
      label: t('data-center:maas.task.step.groups'),
      value: step.groups.join(', '),
    });
  }
  const commandDisplay = formatInitCommandDisplay(step.command);
  if (commandDisplay !== '-') {
    items.push({
      key: 'command',
      label: t('components:startup.command'),
      value: commandDisplay,
    });
  }
  return items;
}

/** 描述区单行文案（label: value 用 · 连接） */
export function buildTaskStepDescriptionText(step: MaasTaskTemplateStep, t: (key: string) => string): string | undefined {
  const items = buildTaskStepDescriptionItems(step, t);
  if (!items.length) return undefined;
  return items.map((item) => `${item.label}: ${item.value}`).join(' · ');
}

interface TaskStepCardProps {
  step: MaasTaskTemplateStep;
  onEdit: () => void;
  onDelete: () => void;
  readonly?: boolean;
  /** 运行态等场景附加在标题后的状态。 */
  status?: ReactNode;
  /** 只读运行态的详情入口。 */
  onDetail?: () => void;
  /** 查看启动命令弹窗标题中的组件名称（如「解码组件（Decode）」） */
  commandModalComponentLabel?: string;
}

export function TaskStepCard({ step, onEdit, onDelete, readonly, status, onDetail, commandModalComponentLabel }: TaskStepCardProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const effectiveReadonly = readonly || sectionReadOnly;
  const metrics = buildTaskStepMetrics(step, t);
  const descriptionText = buildTaskStepDescriptionText(step, t);
  const descriptionItems = buildTaskStepDescriptionItems(step, t);
  const startupCommandItem = descriptionItems.find((item) => item.key === 'command');
  const startupCommandDisplay = startupCommandItem && step.command ? formatNodeStyleCommandMultilineDisplay(step.command) : undefined;
  const readonlyMetaItems = descriptionItems.filter((item) => item.key !== 'command');
  const startupCommandModalTitle = commandModalComponentLabel
    ? formatTaskStepStartupCommandModalTitle(t, commandModalComponentLabel, step.name)
    : step.name;

  return (
    <div className={cardStyles.card}>
      <div className={cardStyles.cardMain}>
        <div className={cardStyles.cardIdentity}>
          <div className={`${cardStyles.cardIcon} ${cardStyles.cardIconWorker}`}>
            <IconFont name="icon-renwu" style={{ fontSize: 24, color: 'rgb(var(--primary-6))' }} />
          </div>
          <div className={cardStyles.cardMeta}>
            <div className={cardStyles.cardTitleRow}>
              <span className={cardStyles.cardName} title={step.name}>
                {step.name}
              </span>
              {status}
            </div>
            {effectiveReadonly ? (
              <>
                {readonlyMetaItems.map((item) => (
                  <div key={item.key} className={cardStyles.cardSubtitleReadonly}>
                    <span className={cardStyles.cardSubtitleLabel}>{item.label}:</span>
                    <span>{item.value}</span>
                  </div>
                ))}
                {startupCommandDisplay && startupCommandDisplay !== '-' ? (
                  <ReadonlyCommandEllipsisRow
                    label={t('components:startup.command')}
                    fullText={startupCommandDisplay}
                    modalTitle={startupCommandModalTitle}
                  />
                ) : null}
              </>
            ) : descriptionText ? (
              <CardSubtitleTooltip tooltip={descriptionText}>{descriptionText}</CardSubtitleTooltip>
            ) : null}
          </div>
        </div>
        {(!effectiveReadonly || onDetail) && (
          <div className={cardStyles.cardActions}>
            {!effectiveReadonly ? (
              <>
                <Button type="text" size="small" icon={<IconEdit />} onClick={onEdit}>
                  {t('common:edit')}
                </Button>
                <Button type="text" size="small" status="danger" icon={<IconDelete />} onClick={onDelete}>
                  {t('common:delete')}
                </Button>
              </>
            ) : null}
            {onDetail ? (
              <Button type="text" size="small" onClick={onDetail}>
                {t('common:detail')}
              </Button>
            ) : null}
          </div>
        )}
      </div>
      <div className={cardStyles.cardMetrics}>
        {metrics.map((item) => (
          <div key={item.key} className={cardStyles.metricItem} title={`${item.label}: ${item.value}`}>
            <span className={cardStyles.metricLabel}>{item.label}</span>
            <span className={cardStyles.metricValue}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface TaskStepAddCardProps {
  onClick: () => void;
}

export function TaskStepAddCard({ onClick }: TaskStepAddCardProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  if (sectionReadOnly) return null;

  return (
    <div
      className={cardStyles.addCard}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={cardStyles.placeholderCard} style={{ border: 'none', padding: 0, minHeight: 'auto', background: 'transparent' }}>
        <span className={cardStyles.placeholderTitle}>{t('data-center:maas.task.step.add')}</span>
      </div>
    </div>
  );
}
